(function (window, document) {
  console.log("MVT Engine: Behavior-track module loaded");

  const CFG = {
    engageDwellMs: 10_000,
    engageScrollFraction: 0.25,
    pageScrollFraction: 0.5,
    rageCount: 3,
    rageWindowMs: 1_000,
    rageRadiusPx: 30,
    deadClickMs: 2_500,
    errorClickMs: 1_000,
    dwellMaxDeltaMs: 60_000,
  };

  function isInteractive(el) {
    try {
      let node = el;
      for (let depth = 0; depth < 5 && node && node.nodeType === 1; depth++) {
        const tag = (node.tagName || "").toUpperCase();
        if (
          tag === "A" ||
          tag === "BUTTON" ||
          tag === "INPUT" ||
          tag === "SELECT" ||
          tag === "TEXTAREA" ||
          tag === "LABEL" ||
          tag === "OPTION" ||
          tag === "SUMMARY"
        ) {
          return true;
        }
        if (typeof node.getAttribute === "function") {
          const role = node.getAttribute("role");
          if (
            role === "button" ||
            role === "link" ||
            role === "checkbox" ||
            role === "tab" ||
            role === "menuitem" ||
            role === "menuitemcheckbox" ||
            role === "radio"
          ) {
            return true;
          }
          if (node.hasAttribute("onclick") || node.hasAttribute("href")) {
            return true;
          }
        }
        if (node.isContentEditable) return true;
        node = node.parentElement;
      }
    } catch (e) {
      return true;
    }
    return false;
  }

  function docScrollHeight() {
    try {
      const b = document.body ? document.body.scrollHeight : 0;
      const d = document.documentElement ? document.documentElement.scrollHeight : 0;
      return Math.max(b, d, 0);
    } catch (e) {
      return 0;
    }
  }

  function normalizeType(raw) {
    const t = String(raw || "").toLowerCase();
    if (t === "dead_click" || t === "dead_unclick") return "dead_click";
    if (t === "page_unload" || t === "unload" || t === "page_leave") {
      return "page_unload";
    }
    if (t === "engagement" || t === "engaged") return "engagement";
    if (t === "bounce") return "bounce";
    if (t === "error_click") return "error_click";
    if (t === "rage_click") return "rage_click";
    if (t === "page_scroll" || t === "scroll") return "page_scroll";
    if (t === "leave_intent" || t === "exit_intent") return "leave_intent";
    return null;
  }

  const SUPPORTED = {
    engagement: true,
    engaged: true,
    bounce: true,
    page_unload: true,
    unload: true,
    page_leave: true,
    dead_click: true,
    dead_unclick: true,
    error_click: true,
    rage_click: true,
    page_scroll: true,
    scroll: true,
    leave_intent: true,
    exit_intent: true,
  };

  const B = {
    _inited: false,
    _handlers: {
      engagement: [],
      bounce: [],
      page_unload: [],
      dead_click: [],
      error_click: [],
      rage_click: [],
      page_scroll: [],
      leave_intent: [],
    },
    _engaged: false,
    _interacted: false,
    _left: false,
    _clicks: [],
    _lastClickMs: 0,
    _dwellMs: 0,
    _lastTickMs: null,
    _dwellTimer: null,

    handles(eventApiName) {
      return !!SUPPORTED[String(eventApiName || "").toLowerCase()];
    },

    arm(eventApiName, metric, fireFn) {
      if (!metric || typeof fireFn !== "function") return;
      const key = normalizeType(eventApiName);
      if (!key || !this._handlers[key]) return;

      const boundKey = `_mvt_bound_${metric.id}`;
      if (window[boundKey]) return;
      window[boundKey] = true;

      this._handlers[key].push(fireFn);
      this._init();

      if (key === "engagement" && this._engaged) {
        try {
          fireFn();
        } catch (e) {}
      }
    },

    _fire(key) {
      const list = this._handlers[key] || [];
      for (let i = 0; i < list.length; i++) {
        try {
          list[i]();
        } catch (e) {}
      }
    },

    _markEngaged() {
      if (this._engaged) return;
      this._engaged = true;
      this._fire("engagement");
    },

    _init() {
      if (this._inited) return;
      this._inited = true;
      const self = this;
      this._lastTickMs = Date.now();

      const onClick = function (e) {
        self._interacted = true;
        self._markEngaged();
        self._handleClick(e);
      };
      const onKey = function () {
        self._interacted = true;
        self._markEngaged();
      };
      const onScroll = function () {
        self._interacted = true;
        try {
          const scrollable = Math.max(0, docScrollHeight() - window.innerHeight);
          if (scrollable <= 0) return;
          const y =
            window.pageYOffset ||
            (document.documentElement && document.documentElement.scrollTop) ||
            0;
          const fraction = y / scrollable;
          if (fraction >= CFG.engageScrollFraction) self._markEngaged();

          if (fraction >= CFG.pageScrollFraction) self._fire("page_scroll");
        } catch (e) {}
      };

      const onMouseOut = function (e) {
        try {
          if (e && !e.relatedTarget && (e.clientY == null || e.clientY <= 0)) {
            self._fire("leave_intent");
          }
        } catch (e2) {}
      };
      const onVisibility = function () {
        self._tickDwell();
        if (document.visibilityState === "hidden") {
          self._onLeave();
        } else if (document.visibilityState === "visible") {
          self._lastTickMs = Date.now();
        }
      };
      const onPageHide = function () {
        self._tickDwell();
        self._onLeave();
      };
      const onError = function () {
        self._handleError();
      };

      try {
        document.addEventListener("click", onClick, true);
        document.addEventListener("keydown", onKey, { passive: true });
        window.addEventListener("scroll", onScroll, { passive: true });
        document.addEventListener("visibilitychange", onVisibility, {
          passive: true,
        });
        window.addEventListener("pagehide", onPageHide, { passive: true });
        window.addEventListener("error", onError, true);
        window.addEventListener("unhandledrejection", onError, true);
        document.addEventListener("mouseout", onMouseOut, { passive: true });
      } catch (e) {}

      try {
        this._dwellTimer = window.setInterval(function () {
          self._tickDwell();
        }, 1_000);
      } catch (e) {}
    },

    _tickDwell() {
      try {
        if (document.visibilityState !== "visible") {
          this._lastTickMs = Date.now();
          return;
        }
        const now = Date.now();
        if (this._lastTickMs) {
          const delta = now - this._lastTickMs;
          if (delta > 0 && delta < CFG.dwellMaxDeltaMs) {
            this._dwellMs += delta;
            if (this._dwellMs >= CFG.engageDwellMs) this._markEngaged();
          }
        }
        this._lastTickMs = now;
      } catch (e) {}
    },

    _onLeave() {
      if (this._left) return;
      this._left = true;

      this._fire("page_unload");
      if (!this._engaged && !this._interacted) {
        this._fire("bounce");
      }
      if (this._dwellTimer) {
        try {
          window.clearInterval(this._dwellTimer);
        } catch (e) {}
        this._dwellTimer = null;
      }
    },

    _handleClick(e) {
      const now = Date.now();
      this._lastClickMs = now;

      let x = 0;
      let y = 0;
      try {
        x = e && typeof e.clientX === "number" ? e.clientX : 0;
        y = e && typeof e.clientY === "number" ? e.clientY : 0;
      } catch (e2) {}
      this._clicks.push({ t: now, x: x, y: y });
      this._clicks = this._clicks.filter((c) => now - c.t <= CFG.rageWindowMs);
      if (this._clicks.length >= CFG.rageCount) {
        const recent = this._clicks.slice(-CFG.rageCount);
        const first = recent[0];
        const tight = recent.every(
          (c) =>
            Math.abs(c.x - first.x) <= CFG.rageRadiusPx &&
            Math.abs(c.y - first.y) <= CFG.rageRadiusPx,
        );
        if (tight) this._fire("rage_click");
      }

      if (this._handlers.dead_click.length > 0 && e) {
        this._detectDeadClick(e);
      }
    },

    _detectDeadClick(e) {
      let target = null;
      try {
        target = e.target;
      } catch (e2) {
        return;
      }

      if (isInteractive(target)) return;

      const self = this;
      const urlBefore = window.location.href;
      let changed = false;

      let observer = null;
      try {
        observer = new MutationObserver(function () {
          changed = true;
        });
        observer.observe(document.documentElement, {
          childList: true,
          subtree: true,
          attributes: true,
          characterData: true,
        });
      } catch (e2) {
        observer = null;
      }

      const onScrollOnce = function () {
        changed = true;
      };
      try {
        window.addEventListener("scroll", onScrollOnce, {
          passive: true,
          once: true,
        });
      } catch (e2) {}

      window.setTimeout(function () {
        try {
          if (observer) observer.disconnect();
        } catch (e2) {}
        try {
          window.removeEventListener("scroll", onScrollOnce);
        } catch (e2) {}
        let navigated = false;
        try {
          navigated = window.location.href !== urlBefore;
        } catch (e2) {}
        let selected = false;
        try {
          selected = !!(window.getSelection && String(window.getSelection()).length > 0);
        } catch (e2) {}
        if (!changed && !navigated && !selected) {
          self._fire("dead_click");
        }
      }, CFG.deadClickMs);
    },

    _handleError() {
      const now = Date.now();
      if (this._lastClickMs && now - this._lastClickMs <= CFG.errorClickMs) {
        this._fire("error_click");
      }
    },
  };

  window.MVTBehaviorTrack = B;
})(window, document);
